import { memo, useEffect, useState } from "react";
import { useTranslate } from "helpers";
import { useHandleTextFieldChange } from "services/forms/hooks";
import { validateYup } from "services/forms/validation/validation";
import { Errors } from "services/forms/validation/types";
import { ContractSellerEditCardProps, ContractSellerEditFormValues } from "./types";
import { initialContractSellerEditForm } from "./consts";
import { CONTRACT_SELLER_EDIT_FORM_SCHEMA } from "./validation";
import { TextField } from "ui/components/TextField/TextField";
import { PhoneTextField } from "ui/components/TextField/PhoneTextField";
import OibTextField from "ui/components/TextField/OibTextField/OibTextField";
import { IbanTextField } from "ui/components/TextField/IbanTextField";
import { Modal } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import { Button } from "ui/components/Button/Button";

const ContractSellerEditCard: React.FC<ContractSellerEditCardProps> = ({
  show,
  formValues,
  errors,
  onSave,
  onClose,
}) => {
  const { t } = useTranslate();
  const [localValues, setLocalValues] = useState<ContractSellerEditFormValues>(initialContractSellerEditForm);
  const [localErrors, setLocalErrors] = useState<Errors>({});

  useEffect(() => {
    if (show) {
      setLocalValues({
        sellerCompanyName: formValues.sellerCompanyName,
        sellerPersonalIdentificationNumber: formValues.sellerPersonalIdentificationNumber,
        sellerHeadquartersAddress: formValues.sellerHeadquartersAddress,
        sellerSalesPersonName: formValues.sellerSalesPersonName,
        sellerPhoneNumber: formValues.sellerPhoneNumber,
        sellerTransactionAccountNumber: formValues.sellerTransactionAccountNumber,
      });
      setLocalErrors({});
    }
  }, [show]);

  const handleTextFieldChange = useHandleTextFieldChange(setLocalValues, localValues);

  const handleSave = () => {
    const isValid = validateYup(localValues, CONTRACT_SELLER_EDIT_FORM_SCHEMA, setLocalErrors);
    if (!isValid) return;

    onSave(localValues);
    onClose();
  };

  return (
    <Modal show={show} onHide={onClose} position="centered" size="small">
      <ModalHeader icon="/media/images/add-icon.svg" title="Uredi prodavatelja" onHide={onClose} />
      <ModalBody>
        <div className="p-4">
          <div className="flex gap-3 mb-6">
            <div className="flex-1">
              <TextField
                type="text"
                name="sellerCompanyName"
                label={t("admin.fleet.vehicle.contract.add.sellercompanyname")}
                placeholder={t("admin.fleet.vehicle.contract.add.sellercompanyname.placeholder")}
                onChange={handleTextFieldChange}
                value={localValues.sellerCompanyName}
                required
                disabled
                error={errors.sellerCompanyName}
              />
            </div>
            <div className="flex-1">
              <OibTextField
                type="text"
                name="sellerPersonalIdentificationNumber"
                label={t("admin.fleet.vehicle.contract.add.sellerpersonalidentificationnumber")}
                placeholder={t("admin.fleet.vehicle.contract.add.sellerpersonalidentificationnumber.placeholder")}
                onChange={handleTextFieldChange}
                value={localValues.sellerPersonalIdentificationNumber}
                required
                disabled
                error={errors.sellerPersonalIdentificationNumber}
              />
            </div>
          </div>
          <div className="flex gap-3 mb-3">
            <div className="flex-1">
              <TextField
                type="text"
                name="sellerHeadquartersAddress"
                label={t("admin.fleet.vehicle.contract.add.sellerheadquartersaddress")}
                placeholder={t("admin.fleet.vehicle.contract.add.sellerheadquartersaddress.placeholder")}
                onChange={handleTextFieldChange}
                value={localValues.sellerHeadquartersAddress}
                required
                 disabled
                error={errors.sellerHeadquartersAddress}
              />
            </div>
            <div className="flex-1">
              <IbanTextField
                type="text"
                name="sellerTransactionAccountNumber"
                label={t("admin.fleet.vehicle.contract.add.sellertransactionaccountnumber")}
                placeholder={t("admin.fleet.vehicle.contract.add.sellertransactionaccountnumber.placeholder")}
                onChange={handleTextFieldChange}
                value={localValues.sellerTransactionAccountNumber}
                required
                 disabled
                error={errors.sellerTransactionAccountNumber}
              />
            </div>
          </div>
          <div className="flex gap-3 mb-6">
            <div className="flex-1">
              <TextField
                type="text"
                name="sellerSalesPersonName"
                label={t("admin.fleet.vehicle.contract.add.sellersalespersonname")}
                placeholder={t("admin.fleet.vehicle.contract.add.sellersalespersonname.placeholder")}
                onChange={handleTextFieldChange}
                value={localValues.sellerSalesPersonName}
                required
                error={localErrors.sellerSalesPersonName || errors.sellerSalesPersonName}
              />
            </div>
            <div className="flex-1">
              <PhoneTextField
                type="text"
                name="sellerPhoneNumber"
                label={t("admin.fleet.vehicle.contract.add.sellerphonenumber")}
                placeholder={t("admin.fleet.vehicle.contract.add.sellerphonenumber.placeholder")}
                onChange={handleTextFieldChange}
                value={localValues.sellerPhoneNumber}
                required
                error={localErrors.sellerPhoneNumber || errors.sellerPhoneNumber}
              />
            </div>
          </div>
          <div className="px-4 py-3 fixed left-0 bottom-0 bg-slate-50 border border-t-slate-200 w-full flex justify-end gap-3">
            <Button variant="outlined" children="Zatvori" onClick={onClose} />
            <Button variant="primary" children="Spremi" onClick={handleSave} />
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default memo(ContractSellerEditCard);
